import { Component } from '@theme/component';

/**
 * Live countdown timer. In fixed mode it ticks down to `end-time` and, on reaching zero,
 * hides its own slide so the announcement bar advances (see _announcement-countdown.liquid
 * and announcement-bar.js).
 *
 * Daily mode (`mode="daily"`) ignores `end-time` and counts down to the visitor's local
 * midnight on every day of an inclusive promo window, rolling into the next day at each
 * midnight and hiding the slide once the window fully passes. `window-start` / `window-end`
 * are calendar dates (`YYYY-MM-DD`) for the first / last promo day; every boundary is
 * evaluated in the visitor's local timezone, so the promo runs on those calendar dates by
 * each visitor's own clock.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [digits]
 * @property {HTMLElement[]} [values]
 *
 * @extends {Component<Refs>}
 */
export class CountdownTimer extends Component {
  static get observedAttributes() {
    return ['end-time', 'mode', 'window-start', 'window-end'];
  }

  /** @type {number|undefined} */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#tick();
    this.#play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#suspend();
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.#tick();
      // An edit may have re-entered the active window; restart the interval if it stopped.
      if (this.#interval === undefined && !document.hidden) this.#play();
    }
  }

  #play() {
    if (this.#interval !== undefined) return;
    this.#interval = setInterval(() => this.#tick(), 1000);
  }

  #suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  get #mode() {
    return this.getAttribute('mode') === 'daily' ? 'daily' : 'fixed';
  }

  /**
   * Local midnight, `offset` days after the local day containing `atMs`, as epoch ms.
   * @param {number} atMs absolute epoch ms
   * @param {number} offset day offset
   * @returns {number} epoch ms of that local midnight
   */
  #localMidnight(atMs, offset = 0) {
    const d = new Date(atMs);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() + offset).getTime();
  }

  /**
   * Parse a `YYYY-MM-DD` window-boundary attribute into that date's local midnight (epoch
   * ms), or null if unset/invalid. Parsed as local calendar parts, not UTC, so the promo
   * runs on these dates by the visitor's own clock.
   * @param {string} name
   * @returns {number|null}
   */
  #windowBoundary(name) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((this.getAttribute(name) ?? '').trim());
    if (!match) return null;

    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
  }

  /**
   * The instant (epoch ms) the current tick counts down to. Its value encodes state for #tick:
   *  - a positive number: keep counting down to this instant
   *  - `0`: expired — trigger on-expiry (daily window fully over)
   *  - `Infinity`: waiting for the daily window to open — clamp at zero, don't expire
   *  - `NaN`: misconfigured/invalid attributes — clamp at zero, don't expire
   * @returns {number}
   */
  #resolveEndTime() {
    if (this.#mode !== 'daily') {
      const time = new Date(this.getAttribute('end-time') ?? '').getTime();
      return time > 0 ? time : NaN;
    }

    const nowMs = Date.now();
    const windowStart = this.#windowBoundary('window-start'); // local midnight of first promo day
    const windowEndDay = this.#windowBoundary('window-end'); // local midnight of last promo day
    if (windowStart === null || windowEndDay === null) return NaN;

    // Inclusive of the last promo day: live until the local midnight after it.
    const windowEnd = this.#localMidnight(windowEndDay, 1);
    if (nowMs < windowStart) return Infinity; // not started yet
    if (nowMs >= windowEnd) return 0; // fully over
    return this.#localMidnight(nowMs, 1); // count down to next local midnight
  }

  #tick() {
    const endTime = this.#resolveEndTime();
    // waiting (Infinity, pre-window) and idle (NaN, misconfigured) both clamp at zero and
    // never expire; only a real elapsed countdown hides the slide.
    const waiting = endTime === Infinity;
    const idle = Number.isNaN(endTime);
    const remaining = waiting || idle ? 0 : Math.max(0, endTime - Date.now());

    const totalSeconds = Math.floor(remaining / 1000);
    const segments = [
      Math.floor(totalSeconds / 86400),
      Math.floor((totalSeconds % 86400) / 3600),
      Math.floor((totalSeconds % 3600) / 60),
      totalSeconds % 60,
    ];

    // Write each unit into its boxed segment (days / hours / minutes / seconds).
    const { values } = this.refs;
    if (Array.isArray(values)) {
      values.forEach((node, index) => {
        node.textContent = String(segments[index] ?? 0).padStart(2, '0');
      });
    }

    if (!waiting && !idle && remaining === 0) this.#handleExpired();
  }

  #handleExpired() {
    this.#suspend();

    // Hide this slide so the announcement bar advances past the finished countdown.
    const slide = this.closest('slideshow-slide');
    if (!slide) return;

    slide.setAttribute('hidden', '');

    // Defer the notification: on an already-expired countdown this runs during our own
    // connectedCallback, before the bar has attached its `slide:hidden` listener (module
    // upgrade order isn't guaranteed). A microtask ensures the bar is listening.
    queueMicrotask(() => {
      this.closest('announcement-bar-component')?.dispatchEvent(
        new CustomEvent('slide:hidden', { bubbles: true })
      );
    });
  }

  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#suspend();
      return;
    }

    // Resume only if there's still something to count down: a future instant or a daily
    // window that hasn't opened yet (Infinity). Expired (0) and idle (NaN) stay stopped.
    const endTime = this.#resolveEndTime();
    if (endTime === Infinity || endTime > Date.now()) {
      this.#tick();
      this.#play();
    }
  };
}

if (!customElements.get('countdown-timer-component')) {
  customElements.define('countdown-timer-component', CountdownTimer);
}
